import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
import type {CopilotFixDialogSubmitOptions} from '@github-ui/copilot-apply-changes-button/types'
import {CopilotFixDialog} from '@github-ui/copilot-apply-changes-button/CopilotFixDialog'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {ActionList, Link} from '@primer/react'
import {type FC, useCallback, useMemo, useState} from 'react'
import {
  getDisplayFilePaths,
  getFindingRenderableFileLocation,
  getFindingSeverity,
  getFindingSourceContextLinks,
  getFindingTitle,
} from './entity-helpers'
import styles from './Finding.module.css'
import type {Finding} from './types'

interface FixFindingDialogProps {
  findings: Finding[]
  basePath: string
  modelsUrl?: string | null
  onClose: () => void
  onTaskCreated?: (ccaTaskId: string, taskUrl: string, findingIds: string[]) => void
}

export const FixFindingDialog: FC<FixFindingDialogProps> = ({
  findings,
  basePath,
  modelsUrl,
  onClose,
  onTaskCreated,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(findings.map(f => f.id)))

  const selectedFindings = useMemo(() => findings.filter(f => selectedIds.has(f.id)), [findings, selectedIds])
  const displayFilePaths = useMemo(
    () => getDisplayFilePaths(findings.filter(f => f.fileUrl).map(f => f.filePath)),
    [findings],
  )

  const handleToggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleSubmit = useCallback(
    async ({createPullRequest, instructions, model}: CopilotFixDialogSubmitOptions) => {
      const body: Record<string, unknown> = {
        create_pull_request: createPullRequest,
        findings: selectedFindings.map(f => ({
          id: f.id,
          message: f.message,
          filePath: f.filePath,
          startLine: f.startLine,
          endLine: f.endLine,
        })),
      }
      if (instructions) body.instructions = instructions
      if (model && model !== 'auto') body.model = model

      const response = await verifiedFetchJSON(`${basePath}/code_review_findings/fix`, {
        method: 'POST',
        body,
      })
      if (!response.ok) {
        let data = null
        try {
          data = await response.json()
        } catch {
          // ignore JSON parse errors
        }
        throw new Error(data?.error || data?.message || 'Failed to create fix task')
      }

      const data = await response.json()
      if (!data?.task_url) {
        throw new Error('No task URL returned')
      }
      if (onTaskCreated && data.task_id && data.finding_ids) {
        onTaskCreated(data.task_id, data.task_url, data.finding_ids)
      }
    },
    [basePath, selectedFindings, onTaskCreated],
  )

  return (
    <CopilotFixDialog
      title="Fix with Copilot"
      onClose={onClose}
      onSubmit={handleSubmit}
      modelsUrl={modelsUrl ?? undefined}
      submitDisabled={selectedIds.size === 0}
      closeOnSubmit
    >
      <div className="tmp-mt-3">
        <p className="tmp-mb-1 text-small text-semibold color-fg-muted">
          {`${selectedIds.size} ${selectedIds.size === 1 ? 'finding' : 'findings'}`}
        </p>
        <ActionList
          showDividers
          role="listbox"
          selectionVariant="multiple"
          aria-label="Findings to fix"
          className="mt-2 border rounded-3"
        >
          {findings.map(f => {
            const severity = getFindingSeverity(f)
            const fileLocation = getFindingRenderableFileLocation(f, displayFilePaths.get(f.filePath))
            const sourceContextLinks = fileLocation ? [] : getFindingSourceContextLinks(f)
            const fileLocationFullPathText =
              fileLocation && fileLocation.fullLocation !== fileLocation.displayLocation ? (
                <span className="sr-only">{` Full path: ${fileLocation.fullLocation}`}</span>
              ) : null
            return (
              <ActionList.Item key={f.id} selected={selectedIds.has(f.id)} onSelect={() => handleToggle(f.id)}>
                <span>{getFindingTitle(f)}</span>
                <ActionList.Description variant="block">
                  <div className="d-flex flex-items-center gap-1">
                    {severity && (
                      <span className={styles.metaItem}>
                        <RuleSeverityBadge severity={severity} />
                      </span>
                    )}
                    {fileLocation && (
                      <span className={styles.metaItem}>
                        <span className="text-mono">
                          <span>{fileLocation.displayLocation}</span>
                          {fileLocationFullPathText}
                        </span>
                      </span>
                    )}
                    {sourceContextLinks.map(link => (
                      <span key={link.url} className={styles.metaItem}>
                        <Link href={link.url} className="Link--inTextBlock">
                          {link.label}
                        </Link>
                      </span>
                    ))}
                  </div>
                </ActionList.Description>
              </ActionList.Item>
            )
          })}
        </ActionList>
      </div>
    </CopilotFixDialog>
  )
}
